import React from 'react';

import styled from '@emotion/styled';

import { BREAKPOINTS, unit } from '@zego/theme';
import useTracking from '@zego/tracking/useTracking';

import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ButtonLink from '@zego/components/button/ButtonLink';
import Link from '@zego/components/Link';
import TrustPilotSimple from '@zego/components/molecules/TrustPilotSimple';
import BaseHero, { HeroProps } from '@zego/components/organisms/Hero';

import Emphasis from '../atoms/Emphasis';

import heroImageLarge from '@zego/static/images/hero-broker-landing-page-large.png';
import heroImageMedium from '@zego/static/images/hero-broker-landing-page-medium.png';

const Container = styled(View)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  [BREAKPOINTS.medium]: {
    maxWidth: unit(49),
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
});

const JoinBrokerNetworkButtonContainer = styled(ButtonLink)({
  width: '270px',
  [BREAKPOINTS.smallOnly]: { width: '100%' },
});

const TitleContainer = styled.span({
  display: 'flex',
  textAlign: 'center',
  [BREAKPOINTS.medium]: {
    textAlign: 'left',
    fontSize: unit(5),
  },
  [BREAKPOINTS.normal]: {
    fontSize: 60,
  },
});

const HERO_COPY: HeroProps = {
  title: (
    <TitleContainer>
      Fleet insurance that saves you time and makes you more money
    </TitleContainer>
  ),
  backgroundImage: [heroImageMedium, heroImageLarge],
  backgroundImageAlt: `Well-dressed man standing in front of a fleet of sedan cars`,
  backgroundImageRatio: [1125 / 528, 1236 / 1600],
  footer: <TrustPilotSimple reviews={2243} />,
};

interface BrokerHeroProps {
  getQuoteTextOnClick: () => void;
}

export default function BrokerHero({
  getQuoteTextOnClick,
}: BrokerHeroProps): JSX.Element {
  const { trackEvent } = useTracking();
  return (
    <BaseHero {...HERO_COPY}>
      <Container>
        <JoinBrokerNetworkButtonContainer
          href="/broker/partner-with-us/"
          onClick={(): void => {
            trackEvent(
              'Web: B2B Broker Landing Page - hero section button clicked',
            );
          }}
        >
          Join our Broker network
        </JoinBrokerNetworkButtonContainer>
        <Spacer height={unit(4)} />
        <Container>
          <RegularText>Already work with Zego?</RegularText>
          <Link>
            <Emphasis
              css={{ textAlign: 'center' }}
              onClick={getQuoteTextOnClick}
            >
              Speak to our experts to get a quote
            </Emphasis>
          </Link>
        </Container>
        <Spacer height={unit(4)} />
      </Container>
    </BaseHero>
  );
}
